import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import { discountShortfall } from "./Order";
import COLORS from "../colors";
import {
  deepFreeze,
  formatCurrency,
  formatPO,
  timestampToDate,
} from "../util/misc";
import { cloneDeep, every, get, isEmpty, isNumber, sortBy } from "lodash";
import { diff } from "deep-object-diff";

export const ACK_FIELDS = {
  orderPO: "",
  vendor: "",
  vendorOrderNumber: "",
  expectedDate: null,
  pendingDate: false,
  totalCost: null,
  documents: [],
  missingDocument: false,
  reason: "",
  directShip: false,
  noMaterial: false,
  reviewed: false,
  reviewedOn: null,
  reviewedBy: null,
  locked: false,
  // The service issue this order's parts are for, when the order is a
  // reorder for one: saving the ack records the order on the issue
  // (LINK_SERVICE_ISSUE_TO_ORDER), so receiving the order receives its parts.
  serviceIssueID: "",

  // Status
  fullyReceived: false,
  fullyBilled: false,
  // What the vendor sync learned about the order number, or null. Set by
  // pollVendorShipping / syncVendorShipping (functions/vendorShippingSync.js):
  // `mismatch` -- Marvin's copy of this number names another sale;
  // `unlisted` -- Marvin ships `suggestedVendorOrderNumber` for this sale,
  // which no ack carries; `unverifiable` -- Marvin's copy of this number is
  // for PO `vendorPO`, which names no sale and is not this order's PO, so it
  // may be another job's order; `contested` -- as `unverifiable`, but
  // something vouches for another order's claim on the number too
  // (`claimedBy`, that order's PO), and its packages are expected there --
  // or, with `held: true`, the two claims are vouched for equally and
  // neither order is fed (planClaimants). A contested ack offers no "This
  // order is ours": the function refuses it.
  // `vendorOrderNumber` is this ack's number when it was checked, so a flag
  // outlives a correction recognisably.
  vendorOrderCheck: null,
  // The office's answer to an `unverifiable` flag (AckView, "This order is
  // ours"): `{ vendorOrderNumber, vendorPO }`, the number and Marvin's PO it
  // was shown. While the ack still carries that number, the sync takes that
  // PO as this order's (unverifiedPOCheck) and stops flagging it, and the
  // dock stops asking about its packages. Written by syncVendorShipping
  // (acceptVendorPO), not the page: a received or billed ack is locked.
  vendorPOAccepted: null,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(ACK_FIELDS);

export const ACK_STATUS_TYPES = {
  review: {
    order: 1,
    label: "Awaiting review",
    icon: "review_pending",
    color: "warning",
  },
  production: {
    order: 2,
    label: "In production",
    icon: "vendor",
    color: "positive",
  },
  billing: {
    order: 3,
    label: "Awaiting bill",
    icon: "bill",
    color: "warning",
  },
  received: {
    order: 4,
    label: "Material received",
    icon: "done",
    color: "positive",
  },
  complete: {
    order: 4,
    label: "Complete",
    icon: "done",
    color: "positive",
  },
};
deepFreeze(ACK_STATUS_TYPES);

export default class Ack extends DataStructure {
  constructor(projectID, saleID, orderID, id, data = {}) {
    // Backward compatibility
    if (!data.orderPO) {
      data.orderPO = projectID + saleID;
    }

    data = sanitize(data, ACK_FIELDS);

    // Convert to Date
    data.expectedDate = timestampToDate(data.expectedDate);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.reviewedOn = timestampToDate(data.reviewedOn);
    if (data.vendorOrderCheck && typeof data.vendorOrderCheck === "object") {
      data.vendorOrderCheck = {
        ...data.vendorOrderCheck,
        checkedAt: timestampToDate(data.vendorOrderCheck.checkedAt),
      };
    } else {
      data.vendorOrderCheck = null;
    }

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.saleID = saleID || null;
    this.orderID = orderID || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
      acceptVendorPO: this.canAcceptVendorPOBy,
    });

    // Documents
    this.data.documents = this.documents.map((d) => d.data);
  }

  // Permissions

  canBeEditedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      (!this.id || !this.locked || this.canBeUnlockedBy(user)) &&
      user.isEmployee
    );
  }

  canBeDeletedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      !this.locked &&
      user.isEmployee &&
      (user.id === this.data.createdBy || user.isAdmin)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  /**
   * Answer an `unverifiable` flag with "This order is ours". The callable
   * writes it (syncVendorShipping's acceptVendorPO), so a locked ack -- one
   * received or billed against, which the page cannot update at all -- can
   * be answered too: only an employee, on a sale that is not locked, as the
   * callable checks.
   */
  canAcceptVendorPOBy(user) {
    const sale = this.sale;
    return Boolean(sale && !sale.locked && user && user.isEmployee);
  }

  // Database

  get path() {
    return Ack.getPath(this.projectID, this.saleID, this.orderID, this.id);
  }

  static getPath(projectID, saleID, orderID, id) {
    let path = `projects/${projectID}/sales/${saleID}/orders/${orderID}/acks`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(ack) {
    return ack.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Ack(
      snapshot.ref.parent.parent.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  get rules() {
    return {
      vendorOrderNumber: [(a) => Boolean(a) || "required"],
      expectedDate: [
        (a) =>
          this.data.noMaterial ||
          Boolean(a || this.data.pendingDate) ||
          "required",
      ],
      totalCost: [(a) => (isNumber(a) && a >= 0) || "totalCostInvalid"],
      documents: [
        (a) =>
          Boolean(
            this.data.missingDocument ||
              (this.data.documents.length &&
                this.documents.every((d) => d.isValid))
          ) || "validDocumentRequired",
      ],
      reason: [
        (a) =>
          Boolean(!this.data.missingDocument || a.trim().length) ||
          "reasonRequiredMissingDocument",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  /**
   * The sync's open question about this ack's order number, or null: the
   * flag it carries, provided it still describes the number on the ack (a
   * corrected number makes the old flag stale until the next sync clears it).
   */
  get vendorOrderIssue() {
    const check = this.data.vendorOrderCheck;
    if (
      !check ||
      !["mismatch", "unlisted", "unverifiable", "contested"].includes(
        check.status
      )
    ) {
      return null;
    }
    const norm = (v) =>
      String(v || "")
        .trim()
        .toUpperCase();
    if (norm(check.vendorOrderNumber) !== norm(this.data.vendorOrderNumber)) {
      return null;
    }
    // Answered by the office, before the sync has caught up.
    const accepted = this.data.vendorPOAccepted;
    if (
      check.status === "unverifiable" &&
      accepted &&
      norm(accepted.vendorOrderNumber) === norm(check.vendorOrderNumber) &&
      norm(accepted.vendorPO) === norm(check.vendorPO)
    ) {
      return null;
    }
    return check;
  }

  get isFinalAck() {
    const sale = this.sale;
    // Optional all the way down: an order only gets a `status.orders` entry
    // from SAVE_ORDER, so orders predating that code have none, and
    // DELETE_ORDER removes the entry while the ack can linger as an orphan.
    // An unguarded read throws, and a throwing getter reached from a computed
    // takes the whole render down with it.
    return this.id && sale
      ? sale.status.orders?.[this.orderID]?.fullyAcked ?? null
      : null;
  }

  // Compares the acknowledged cost against the order's discounted target when
  // an additional discount was expected. `flag` is true when the discount is
  // not clearly reflected — note some manufacturers only apply the discount at
  // billing, so an ack shortfall may still clear once the bill arrives.
  get discountReview() {
    const order = this.order;
    if (!order || !order.expectsAdditionalDiscount) {
      return null;
    }
    const target = order.discountedCostTarget;
    const actual = this.data.totalCost;
    if (!isNumber(actual) || target === null) {
      return null;
    }
    return {
      flag: discountShortfall(actual, target),
      target,
      actual,
      percent: order.data.additionalDiscountPercent,
    };
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(diff(Ack.sanitize({ ...this.data, vendor: "" }), ACK_FIELDS))
    );
  }

  isEqual(ack) {
    return (
      this.id === ack.id &&
      isEmpty(
        diff(
          Ack.sanitize(cloneDeep(this.data)),
          Ack.sanitize(cloneDeep(ack.data))
        )
      )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    data.documents = data.documents.filter(
      (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
    );
    return data;
  }

  static sanitize(data, removeUnsaved) {
    if (data.missingDocument) {
      data.documents = [];
    } else {
      data.reason = "";
    }
    Ack.cleanDocuments(data, removeUnsaved);
    // A cleared picker says null; the field's empty value is "".
    data.serviceIssueID = data.serviceIssueID || "";
    return data;
  }

  // Formatting

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }

  get sale() {
    return this.saleID
      ? store.state.main.sales[this.projectID + this.saleID]
      : null;
  }

  get po() {
    return formatPO(this.projectID + this.saleID, this.data.orderPO);
  }

  get order() {
    return this.orderID ? store.state.main.orders[this.orderID] : null;
  }

  get serviceIssue() {
    return this.data.serviceIssueID
      ? store.state.main.serviceIssues[this.data.serviceIssueID] || null
      : null;
  }

  get bills() {
    return sortBy(
      Object.values(store.state.main.bills).filter(
        (bill) => bill.ackID === this.id
      ),
      "created"
    );
  }

  get packingSlips() {
    return sortBy(
      Object.values(store.state.main.packingSlips).filter(
        (packingSlip) => packingSlip.ackID === this.id
      ),
      "created"
    );
  }

  get document() {
    return this.data.documents.length ? this.documents[0] : null;
  }

  get documents() {
    return sortBy(
      this.data.documents.map((d) => new DocumentRef(d)),
      "file.modified"
    ).reverse();
  }

  get totalCost() {
    return this.data.totalCost !== null
      ? formatCurrency(this.data.totalCost)
      : null;
  }

  get reviewedBy() {
    return this.data.reviewedBy
      ? store.state.main.users[this.data.reviewedBy]
      : null;
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get status() {
    if (
      !this.data.reviewed &&
      (!this.data.fullyReceived || this.data.noMaterial) &&
      !this.data.missingDocument &&
      !this.data.locked
    ) {
      return "review";
    } else if (!this.data.fullyReceived && !this.data.noMaterial) {
      return "production";
    } else if (!this.data.fullyBilled) {
      return "billing";
    } else {
      return this.data.noMaterial ? "complete" : "received";
    }
  }

  get statusType() {
    const type = ACK_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }
}
